import * as React from "react";
import { RichTextInput } from "ra-input-rich-text";
import {
  ArrayInput,
  AutocompleteInput,
  ReferenceInput,
  SimpleFormIterator,
  TextInput,
  TranslatableInputs,
  useLocaleState,
  useTranslate,
} from "react-admin";
import { Typography } from "@mui/material";
import RichToolbar from "../creatorsShared/RichToolbar";
import SectionPaper from "../creatorsShared/SectionPaper";
import { formatImage } from "../creatorsShared/formatImage";
import ImageCropInput from "../creatorsShared/imageCrop/ImageCropInput";

const PreviewBuilderForm = () => {
  const [locale] = useLocaleState();
  const translate = useTranslate();

  return (
    <div>
      <SectionPaper title={translate("preview.sections.service")}>
        <span>{translate("services.cover")}</span>
        <ImageCropInput
          slot="serviceCover"
          fullWidth
          sx={{ display: "grid", gridTemplateColumns: " 1.5fr 0fr 1fr" }}
          label={false}
          source="service.cover"
          format={formatImage}
        />

        <TranslatableInputs fullWidth defaultLocale={locale} locales={["pl"]}>
          <TextInput
            fullWidth
            source="service.serviceName"
            label="services.serviceName"
          />
          <TextInput
            fullWidth
            source="service.coverTitle"
            label="services.coverTitle"
          />
          <TextInput
            fullWidth
            source="service.coverSubTitle"
            label="services.coverSubTitle"
          />
          <TextInput
            fullWidth
            source="service.mainPriceTitle"
            label="services.mainPriceTitle"
          />
          <TextInput
            fullWidth
            source="service.mainPriceSubtitle"
            label="services.mainPriceSubtitle"
          />
        </TranslatableInputs>
      </SectionPaper>

      <SectionPaper title={translate("preview.sections.banner")}>
        <ImageCropInput
          slot="banner"
          fullWidth
          sx={{ display: "grid", gridTemplateColumns: " 1.5fr 0fr 1fr" }}
          label={false}
          source="banner.bannerImage"
          format={formatImage}
        />
        <TranslatableInputs fullWidth defaultLocale={locale} locales={["pl"]}>
          <TextInput
            fullWidth
            source="banner.bannerTitle"
            label="banners.title"
          />
          <TextInput
            fullWidth
            source="banner.bannerSubtitle"
            label="banners.subtitle"
          />
          <ArrayInput source="banner.bannerSlogans" label="banners.slogans">
            <SimpleFormIterator fullWidth>
              <RichTextInput
                toolbar={<RichToolbar />}
                fullWidth
                source="slogan"
                label="banners.slogan"
              />
            </SimpleFormIterator>
          </ArrayInput>
        </TranslatableInputs>
      </SectionPaper>

      <SectionPaper title={translate("preview.sections.forms")}>
        <ArrayInput source="forms" label={false}>
          <SimpleFormIterator fullWidth inline={false}>
            {/* carries the existing Form id when editing, so save can update in place instead of creating a duplicate */}
            <TextInput
              source="formId"
              label={false}
              sx={{ display: "none" }}
            />
            <ImageCropInput
              slot="formImage"
              fullWidth
              sx={{ display: "grid", gridTemplateColumns: " 1.5fr 0fr 1fr" }}
              label={false}
              source="cover.formImage"
              format={formatImage}
            />
            <TextInput fullWidth source="cover.formVideo" label="forms.video" />

            {/*
              TranslatableInputs can't be nested inside an ArrayInput: it
              clones its direct children with an absolute "source.locale"
              path (react-admin's TranslatableInputsTabContent), ignoring
              the ambient "forms.N." prefix from SimpleFormIterator. Since
              this app only ever populates the "pl" locale (see other
              Create pages), each field is bound to "<field>.pl" by hand
              instead, so the ambient array-index prefix still applies.
            */}
            <Typography variant="caption" color="text.secondary">
              PL
            </Typography>
            <TextInput fullWidth source="formTitle.pl" label="forms.title" />
            <TextInput
              fullWidth
              source="formSubtitle.pl"
              label="forms.subtitle"
            />
            <RichTextInput
              toolbar={<RichToolbar />}
              fullWidth
              source="formDescription.pl"
              label="forms.description"
            />
            <TextInput
              fullWidth
              source="formArticle.pl"
              label="forms.article"
            />

            <ArrayInput source="extraButtons.pl" label="forms.extraButtons">
              <SimpleFormIterator fullWidth>
                <TextInput
                  fullWidth
                  source="title"
                  label="forms.buttonTitle"
                />
                <ReferenceInput source="link" reference="services">
                  <AutocompleteInput
                    fullWidth
                    label="forms.buttonLink"
                    optionText={`serviceName.${locale}`}
                  />
                </ReferenceInput>
              </SimpleFormIterator>
            </ArrayInput>

            <ArrayInput source="prices.pl" label="services.prices">
              <SimpleFormIterator fullWidth>
                <TextInput
                  fullWidth
                  source="categoryTitle"
                  label="services.categoryTitle"
                />
                <ArrayInput source="priceList" label="services.priceList">
                  <SimpleFormIterator fullWidth>
                    <TextInput
                      fullWidth
                      source="name"
                      label="services.name"
                    />
                    <TextInput
                      fullWidth
                      source="price"
                      label="services.price"
                    />
                  </SimpleFormIterator>
                </ArrayInput>
              </SimpleFormIterator>
            </ArrayInput>
          </SimpleFormIterator>
        </ArrayInput>
      </SectionPaper>
    </div>
  );
};

export default PreviewBuilderForm;
